import axios from 'axios';
import { createMutation, createQuery } from 'react-query-kit';

import { getInjector } from '@/services/angularjs-bridge';
import { queryClient } from '@/services/query-client';
import { restUrl } from '@/services/utils';

import { sortUsers, userSalutation } from './user-management.format';
import {
    BillingAddress,
    LicenseProfile,
    OrderedPlanProfile,
    PendingInvitation,
    RestrictionFilterOption,
    RoleDefinition,
    UsageLimits,
    UserAccount,
    UserGroup,
} from './user-management.types';

const ROOT_KEY = ['user-management'] as const;

export function refreshUserManagementLists() {
    getInjector().get('UsersGroupService').reloadUsersAndGroups();
    queryClient.invalidateQueries({ queryKey: [...ROOT_KEY] });
}

export const useUsers = createQuery<UserAccount[], { currentUserId?: string }>({
    queryKey: [...ROOT_KEY, 'users'],
    fetcher: async ({ currentUserId }) => {
        const response = await axios.get<UserAccount[]>(restUrl('users'));
        const users = (response.data ?? []).map((user) => ({
            ...user,
            owner: Boolean(currentUserId) && String(user.userId) === String(currentUserId),
            displayName: userSalutation(user),
        }));
        return sortUsers(users);
    },
});

export const usePendingInvitations = createQuery<PendingInvitation[], void>({
    queryKey: [...ROOT_KEY, 'invitations'],
    fetcher: async () => {
        const response = await axios.get<PendingInvitation[]>(restUrl('invitations'), {
            params: {
                state: 'PENDING',
                type: 'INVITED_COLLEAGUE',
            },
        });
        return response.data ?? [];
    },
});

export const useUsageLimits = createQuery<UsageLimits, void>({
    queryKey: [...ROOT_KEY, 'usage-limits'],
    fetcher: async () => {
        const response = await axios.get<UsageLimits>(restUrl('usagelimits'));
        return response.data ?? {};
    },
});

export const useRoleNames = createQuery<string[], void>({
    queryKey: [...ROOT_KEY, 'role-names'],
    fetcher: async () => {
        const response = await axios.get<string[]>(restUrl('rolenames'), {
            params: { type: 'non-system' },
        });
        return response.data ?? [];
    },
});

export const useRoleDefinitions = createQuery<Record<string, RoleDefinition>, void>({
    queryKey: [...ROOT_KEY, 'role-definitions'],
    fetcher: async () => {
        const response = await axios.get<RoleDefinition[]>(restUrl('roles'));
        return Object.fromEntries((response.data ?? []).map((role) => [role.name, role]));
    },
});

export const useUserGroups = createQuery<UserGroup[], void>({
    queryKey: [...ROOT_KEY, 'groups'],
    fetcher: async () => {
        const response = await axios.get<UserGroup[]>(restUrl('userGroups'));
        return response.data ?? [];
    },
});

export const useRestrictionFilters = createQuery<RestrictionFilterOption[], void>({
    queryKey: [...ROOT_KEY, 'filters'],
    fetcher: async () => {
        const response = await axios.get<Array<{ name: string; label?: string }>>(restUrl('user/filters'));
        return (response.data ?? []).map((filter) => ({
            name: filter.name,
            label: filter.label || filter.name,
        }));
    },
});

export const useAdditionalLicenseProfiles = createQuery<LicenseProfile[], void>({
    queryKey: [...ROOT_KEY, 'license-profiles'],
    fetcher: async () => {
        const response = await axios.get<LicenseProfile[]>(restUrl('billing/additionalLicenseProfiles'), {
            params: { licenseType: 'ADDITIONAL_USERS' },
        });
        return response.data ?? [];
    },
});

export const useOrderedPlanProfile = createQuery<OrderedPlanProfile, void>({
    queryKey: [...ROOT_KEY, 'ordered-plan'],
    fetcher: async () => {
        const response = await axios.get<OrderedPlanProfile>(restUrl('billing/orderedPlanProfile'));
        return response.data ?? {};
    },
});

export const useBillingAddress = createQuery<BillingAddress, void>({
    queryKey: [...ROOT_KEY, 'billing-address'],
    fetcher: async () => {
        const response = await axios.get<BillingAddress>(restUrl('billing/billingAddress'));
        return response.data ?? {};
    },
});

interface InvitePayload {
    invitedEmailAddress: string;
    roles: string[];
    restrictionFilter?: string;
    restrictionQuery?: string;
}

export const useInviteColleague = createMutation<unknown, InvitePayload>({
    mutationFn: async (invitation) => {
        const response = await axios.post(restUrl('invitations'), {
            invitationType: 'INVITED_COLLEAGUE',
            ...invitation,
        });
        return response.data;
    },
});

export const useUpdateInvitation = createMutation<unknown, PendingInvitation>({
    mutationFn: async (invitation) => {
        const response = await axios.post(restUrl('invitations'), invitation);
        return response.data;
    },
});

export const useDeleteInvitation = createMutation<unknown, { invitationId: number }>({
    mutationFn: async ({ invitationId }) => {
        const response = await axios.delete(restUrl(`invitations/${invitationId}`));
        return response.data;
    },
});

export const useDeactivateUser = createMutation<unknown, { accountId: number }>({
    mutationFn: async ({ accountId }) => {
        const response = await axios.put(restUrl(`users/${accountId}/active/false`));
        return response.data;
    },
});

interface UpdateUserPayload {
    accountId: number;
    restrictionQuery?: string;
    restrictionFilter?: string;
    groupIds: Array<string | number>;
    roles?: string[];
}

export const useUpdateUser = createMutation<unknown, UpdateUserPayload>({
    mutationFn: async ({ accountId, ...payload }) => {
        const response = await axios.put(restUrl(`users/${accountId}`), payload);
        return response.data;
    },
});

interface BuyLicensePayload {
    additionalLicense: string;
    currency: string;
    billingAddress: BillingAddress;
}

export const useBuyLicenses = createMutation<unknown, BuyLicensePayload>({
    mutationFn: async (payload) => {
        const response = await axios.post(restUrl('billing/addAdditionalLicense'), payload);
        return response.data;
    },
});

export async function fetchUserLimit(): Promise<number | undefined> {
    const response = await axios.get<UsageLimits>(restUrl('usagelimits'));
    return response.data?.limits?.USERS;
}

export async function downloadUsersFile(): Promise<void> {
    const response = await axios.get<ArrayBuffer>(restUrl('exportusers'), {
        responseType: 'arraybuffer',
    });
    const disposition = String(response.headers['content-disposition'] ?? '');
    const filename = (disposition.split('=')[1] ?? 'users.xls').trim().replace(/"/g, '');
    const blob = new Blob([response.data], { type: 'application/vnd.ms-excel' });
    const url = window.URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = filename;
    anchor.style.display = 'none';
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    window.URL.revokeObjectURL(url);
}
